/**
 * Accordion component with smooth height animations using Web Animations API (WAAPI)
 *
 * Usage:
 * <accordion-component>
 *   <details>
 *     <summary>Title</summary>
 *     <div class="accordion__content">Content</div>
 *   </details>
 * </accordion-component>
 *
 * Guarded because component-accordion.js is included from multiple snippets that can
 * all render on the same page (cart-drawer, main-cart-items, footer).
 */
if (!customElements.get('accordion-component')) {
  customElements.define(
    'accordion-component',
    class Accordion extends HTMLElement {
      constructor() {
        super();
        this.el = this.querySelector('details');
        if (!this.el) return;

        this.summary = this.el.querySelector('summary');
        this.content = this.el.querySelector('summary + *');

        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;

        this.summary.addEventListener('click', (e) => this.onClick(e));
      }

      onClick(e) {
        e.preventDefault();
        this.el.style.overflow = 'hidden';

        if (this.isClosing || !this.el.open) {
          this.closeSiblings();
          this.open();
        } else if (this.isExpanding || this.el.open) {
          this.shrink();
        }
      }

      shrink() {
        this.isClosing = true;
        this.el.style.overflow = 'hidden';

        const startHeight = `${this.el.offsetHeight}px`;
        const endHeight = `${this.summary.offsetHeight}px`;

        if (this.animation) {
          this.animation.cancel();
        }

        this.animation = this.el.animate({ height: [startHeight, endHeight] }, { duration: 300, easing: 'ease-out' });

        this.animation.onfinish = () => this.onAnimationFinish(false);
        this.animation.oncancel = () => (this.isClosing = false);
      }

      open() {
        this.el.style.height = `${this.el.offsetHeight}px`;
        this.el.open = true;
        window.requestAnimationFrame(() => this.expand());
      }

      closeSiblings() {
        const name = this.el.getAttribute('name');
        if (!name) return;
        document.querySelectorAll(`accordion-component details[name="${name}"]`).forEach((details) => {
          if (details === this.el || !details.open) return;
          const sibling = details.closest('accordion-component');
          if (sibling) sibling.shrink();
        });
      }

      expand() {
        this.isExpanding = true;

        const startHeight = `${this.el.offsetHeight}px`;
        const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

        if (this.animation) {
          this.animation.cancel();
        }

        this.animation = this.el.animate({ height: [startHeight, endHeight] }, { duration: 300, easing: 'ease-out' });

        this.animation.onfinish = () => this.onAnimationFinish(true);
        this.animation.oncancel = () => (this.isExpanding = false);
      }

      onAnimationFinish(open) {
        this.el.open = open;
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
        this.el.style.height = '';
        this.el.style.overflow = '';
      }
    }
  );
}
